import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Toast from '../components/Toast/Toast';
import styles from '../components/Toast/ToastContainer.module.css';

// Separate contexts for API and state to prevent unnecessary re-renders
const ToastAPIContext = createContext();
const ToastStateContext = createContext();

// Constants
const MAX_VISIBLE_TOASTS = 4;
const DEDUPLICATION_WINDOW = 5000; // 5 seconds
const DEFAULT_DURATION = {
  success: 5000,
  info: 5000,
  warning: 6000,
  error: 0, // Persist until dismissed
};

// Toast reducer actions
const ACTIONS = {
  ADD_TOAST: 'ADD_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
};

// Generate unique IDs
const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Toast reducer
function toastReducer(state, action) {
  switch (action.type) {
    case ACTIONS.ADD_TOAST: {
      const { message, type, duration, id } = action.payload;
      
      // Check for duplicate messages within deduplication window
      const now = Date.now();
      const duplicate = state.find(
        toast =>
          toast.message === message &&
          toast.type === type &&
          now - toast.timestamp < DEDUPLICATION_WINDOW
      );
      
      if (duplicate) {
        // Update count on duplicate toast
        return state.map(toast =>
          toast.id === duplicate.id
            ? { ...toast, count: (toast.count || 1) + 1, timestamp: now }
            : toast
        );
      }
      
      // Add new toast
      const newToast = {
        id,
        message,
        type,
        duration,
        timestamp: now,
        count: 1,
      };
      
      // FIFO queue management - keep only MAX_VISIBLE_TOASTS
      const newState = [...state, newToast];
      if (newState.length > MAX_VISIBLE_TOASTS) {
        return newState.slice(-MAX_VISIBLE_TOASTS);
      }
      
      return newState;
    }
    
    case ACTIONS.REMOVE_TOAST:
      return state.filter(toast => toast.id !== action.payload);
    
    case ACTIONS.UPDATE_TOAST:
      return state.map(toast =>
        toast.id === action.payload.id
          ? { ...toast, ...action.payload.updates }
          : toast
      );
    
    default:
      return state;
  }
}

// Toast Stack Component (only subscriber to state)
const ToastStack = () => {
  const toasts = useContext(ToastStateContext);
  const { removeToast } = useContext(ToastAPIContext);
  
  if (toasts.length === 0) return null;
  
  return createPortal(
    <div className={styles.container} role="region" aria-label="Notifications">
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          toast={toast}
          onRemove={removeToast}
        />
      ))}
    </div>,
    document.body
  );
};

// Provider component
export const ToastProvider = ({ children }) => {
  const [toasts, dispatch] = useReducer(toastReducer, []);
  
  const addToast = useCallback((message, type = 'info', customDuration) => {
    const id = generateId();
    const duration = customDuration !== undefined ? customDuration : (DEFAULT_DURATION[type] ?? DEFAULT_DURATION.info);
    
    dispatch({
      type: ACTIONS.ADD_TOAST,
      payload: { id, message, type, duration },
    });
    
    return id;
  }, []);
  
  const removeToast = useCallback((id) => {
    dispatch({ type: ACTIONS.REMOVE_TOAST, payload: id });
  }, []);
  
  const updateToast = useCallback((id, updates) => {
    dispatch({
      type: ACTIONS.UPDATE_TOAST,
      payload: { id, updates },
    });
  }, []);
  
  // Convenience methods
  const error = useCallback((message, duration) => {
    return addToast(message, 'error', duration);
  }, [addToast]);
  
  const success = useCallback((message, duration) => {
    return addToast(message, 'success', duration);
  }, [addToast]);
  
  const info = useCallback((message, duration) => {
    return addToast(message, 'info', duration);
  }, [addToast]);
  
  const warning = useCallback((message, duration) => {
    return addToast(message, 'warning', duration);
  }, [addToast]);
  
  const api = {
    addToast,
    removeToast,
    updateToast,
    error,
    success,
    info,
    warning,
  };
  
  return (
    <ToastAPIContext.Provider value={api}>
      <ToastStateContext.Provider value={toasts}>
        {children}
        <ToastStack />
      </ToastStateContext.Provider>
    </ToastAPIContext.Provider>
  );
};

// Hook to use toast API (most common usage)
export const useToast = () => {
  const context = useContext(ToastAPIContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Hook to access toast state (rarely needed)
export const useToastState = () => {
  const context = useContext(ToastStateContext);
  if (!context) {
    throw new Error('useToastState must be used within a ToastProvider');
  }
  return context;
};